import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Button,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { Delete, Error } from "@mui/icons-material";
import { themes } from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import sessionHandling from "services/utils/notificationUtils";
import { CustomButton } from "components/CustomComponents/CustomButton";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { usePayrollCycleRun } from "../../PayrollCycleRunContext";

const importedDataFilterHeaders = [
  { name: "Employee ID", key: "user__employee_id", type: "string" },
  { name: "Employee Name", key: "user__auth_user__first_name", type: "string" },
  { name: "Component", key: "component", type: "string" },
  { name: "Category", key: "component_category", type: "string" },
  { name: "Amount", key: "total", type: "number" },
  { name: "Basis", key: "basis", type: "string" },
  { name: "Bulk Import", key: "component_name", type: "string" },
  { name: "Imported On", key: "import_date", type: "date", showBetweenOperator: true },
];

export const ImportedData = () => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, token } = useSelector((state) => state.tokenReducer);
  const { payScheduleId } = usePayrollCycleRun();

  const [importedData, setImportedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null, // 'single' or 'bulk'
    record: null,
    bulkImportId: null,
  });

  // Fetch imported bulk data
  const fetchImportedData = useCallback(async () => {
    if (!payScheduleId) return;

    try {
      setLoading(true);
      const response = await fetch(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/get_imported_bulk_data/?payroll_id=${payScheduleId}&company_id=${requestCompanyId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setImportedData(data);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        showToast("error", "Failed to fetch imported data");
      }
    } catch (error) {
      console.error('[Importeddata] operation failed:', error);
      showToast("error", "Error fetching imported data");
    } finally {
      setLoading(false);
    }
  }, [domain, token, payScheduleId, requestCompanyId]);

  useEffect(() => {
    fetchImportedData();
  }, [fetchImportedData]);

  const visibleImportedBulkData = (
    importedData?.imported_bulk_data || []
  ).filter((bulkImport) => bulkImport.earning_summaries?.length > 0);

  const allRecords = visibleImportedBulkData.flatMap((bulkImport) =>
    bulkImport.earning_summaries.map((record) => ({
      ...record,
      bulk_import_id: bulkImport.bulk_import_id,
      component_name: bulkImport.component_name,
      import_date: bulkImport.import_date,
    })),
  );

  const allSelected =
    filteredRecords.length > 0 &&
    filteredRecords.every((record) => selectedRecords.includes(record.id));
  const someSelected =
    filteredRecords.some((record) => selectedRecords.includes(record.id)) &&
    !allSelected;

  useEffect(() => {
    setFilteredRecords(allRecords);
  }, [allRecords]);

  const handleFilteredDataChange = useCallback((filteredData) => {
    setFilteredRecords(filteredData);
  }, []);

  // Handle single record selection
  const handleRecordSelect = (recordId) => {
    setSelectedRecords((prev) =>
      prev.includes(recordId)
        ? prev.filter((id) => id !== recordId)
        : [...prev, recordId],
    );
  };

  // Handle select all records in the table
  const handleSelectAllRecords = () => {
    if (allSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecords.map((record) => record.id));
    }
  };

  // Delete single record
  const handleDeleteSingle = async (record) => {
    try {
      const response = await fetch(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/remove_other_earnings/?company_id=${requestCompanyId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify({
            pay_schedule_id: payScheduleId,
            other_earning_id: record.id,
            user_id: record.user_id || record.user,
          }),
        },
      );

      if (response.ok) {
        showToast("success", "Record deleted.");
        fetchImportedData(); // Refresh data
        setSelectedRecords((prev) => prev.filter((id) => id !== record.id));
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        showToast("error", "Failed to delete record");
      }
    } catch (error) {
      console.error('[Importeddata] operation failed:', error);
      showToast("error", "Error deleting record");
    }
  };

  // Delete bulk records
  const handleDeleteBulk = async () => {
    try {
      const recordsToDelete = importedData.imported_bulk_data.flatMap(
        (bulkImport) =>
          bulkImport.earning_summaries.filter((record) =>
            selectedRecords.includes(record.id),
          ),
      );

      let successCount = 0;
      let failureCount = 0;

      // Delete each record individually using the single delete API
      for (const record of recordsToDelete) {
        try {
          const response = await fetch(
            `${domain}payroll/operations/payrollprocessing/payrollruncycle/remove_other_earnings/?company_id=${requestCompanyId}`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Authorization: `token ${token}`,
              },
              body: JSON.stringify({
                pay_schedule_id: payScheduleId,
                other_earning_id: record.id,
                user_id: record.user_id || record.user,
              }),
            },
          );

          if (response.ok) {
            successCount++;
          } else if (response.status === 409) {
            sessionHandling();
            failureCount++;
          } else {
            failureCount++;
          }
        } catch (error) {
          failureCount++;
        }
      }

      if (successCount > 0) {
        showToast(
          "success",
          `${successCount} record${successCount !== 1 ? "s" : ""} deleted successfully`,
        );
        fetchImportedData(); // Refresh data
        setSelectedRecords([]);
      }

      if (failureCount > 0) {
        showToast("error", `Failed to delete ${failureCount} record(s)`);
      }
    } catch (error) {
      console.error('[Importeddata] operation failed:', error);
      showToast("error", "Error deleting records");
    }
  };

  // Delete entire bulk import group
  const handleDeleteBulkImport = async (bulkImportId) => {
    try {
      const response = await fetch(
        `${domain}payroll/operations/payrollprocessing/payrollruncycle/delete_bulk/?company_id=${requestCompanyId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `token ${token}`,
          },
          body: JSON.stringify({ bulk_import_id: bulkImportId }),
        },
      );

      if (response.ok) {
        showToast("success", "Bulk import deleted.");
        fetchImportedData(); // Refresh data
        setSelectedRecords([]);
      } else if (response.status === 409) {
        sessionHandling();
      } else {
        showToast("error", "Failed to delete bulk import");
      }
    } catch (error) {
      console.error('[Importeddata] operation failed:', error);
      showToast("error", "Error deleting bulk import");
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (type, record = null, bulkImportId = null) => {
    setDeleteDialog({
      open: true,
      type,
      record,
      bulkImportId,
    });
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      type: null,
      record: null,
      bulkImportId: null,
    });
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (deleteDialog.type === "single" && deleteDialog.record) {
      await handleDeleteSingle(deleteDialog.record);
    } else if (deleteDialog.type === "bulk") {
      await handleDeleteBulk();
    } else if (deleteDialog.type === "bulk_import" && deleteDialog.bulkImportId) {
      await handleDeleteBulkImport(deleteDialog.bulkImportId);
    }
    closeDeleteDialog();
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (
    !importedData ||
    !importedData.imported_bulk_data ||
    visibleImportedBulkData.length === 0
  ) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">No data available</Alert>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Imported Bulk Data
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {importedData.payroll_name} • {importedData.total_components}{" "}
          components • {importedData.total_records} records
        </Typography>
        {/* Bulk Actions */}
        {selectedRecords.length > 0 && (
          <Paper
            sx={{
              p: 2,
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              border: "1px solid rgba(244, 67, 54, 0.3)",
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedRecords.length} record
                {selectedRecords.length !== 1 ? "s" : ""} selected
              </Typography>
              <CustomButton
                actionName="Delete Selected"
                actionFuntion={() => openDeleteDialog("bulk")}
                customTitleSx={{
                  textTransform: "none",
                  backgroundColor: "#f44336",
                  "&:hover": { backgroundColor: "#d32f2f" },
                }}
              />
            </Stack>
          </Paper>
        )}
      </Box>

      <CustomFilter
        title="Imported Data"
        filterHeaders={importedDataFilterHeaders}
        data={allRecords}
        filteredData={filteredRecords}
        onFilteredDataChange={handleFilteredDataChange}
        showDownloadOptions
        screenAccess={true}
      />

      {/* Combined Records Table */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          border: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: themes?.whiteColor }}>
                <TableCell sx={{ fontWeight: 700, width: 50 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={handleSelectAllRecords}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Employee ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Employee Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Component</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Basis</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Imported On</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 100 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((record) => (
                <TableRow
                  key={`${record.bulk_import_id}-${record.id}`}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "rgba(0, 0, 0, 0.02)",
                    },
                  }}
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedRecords.includes(record.id)}
                      onChange={() => handleRecordSelect(record.id)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{record.user__employee_id}</TableCell>
                  <TableCell>{record.user__auth_user__first_name}</TableCell>
                  <TableCell>{record.component}</TableCell>
                  <TableCell>{record.component_category}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    ₹{record.total?.toLocaleString()}
                  </TableCell>
                  <TableCell>{record.basis}</TableCell>
                  <TableCell>
                    {new Date(record.import_date).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => openDeleteDialog("single", record)}
                      sx={{ color: "#f44336" }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Error sx={{ color: "#f44336" }} />
            <Typography variant="h6">Confirm Delete</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography>
            {deleteDialog.type === "single"
              ? `Are you sure you want to delete the earning record for ${deleteDialog.record?.user__auth_user__first_name} ${deleteDialog.record?.user__auth_user__last_name}?`
              : deleteDialog.type === "bulk_import"
                ? `Are you sure you want to delete this entire bulk import (ID: ${deleteDialog.bulkImportId})? All ${importedData?.imported_bulk_data.find(b => b.bulk_import_id === deleteDialog.bulkImportId)?.earning_summaries?.length || 0} records will be removed.`
                : `Are you sure you want to delete ${selectedRecords.length} selected earning record${selectedRecords.length !== 1 ? "s" : ""}?`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<Delete />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
